import { Injectable } from '@angular/core';
import { BaseService } from '@mobilefirstdev/base-angular';
import { Observable, combineLatest, of } from 'rxjs';
import { catchError, map, shareReplay, switchMap } from 'rxjs/operators';
import { Asset } from '../models/image/dto/asset';
import { AssetLibraryType } from '../models/utils/dto/asset-library-type';
import { CompanyDomainModel } from '../domainModels/company-domain-model';
import { MediaManagerDomainModel } from '../domainModels/media-manager/media-manager-domain-model';
import { ProductDomainModel } from '../domainModels/product-domain-model';
import { ProductSignalUser } from '../models/product/dto/product-signal/product-signal-user';
import { Variant } from '../models/product/dto/variant';
import { exists } from '../functions/exists';

/**
 * The material behind the HighGuide Insights empty states.
 *
 * A company with no recommendations yet sees a blurred mock-up of the widgets rather than empty space, so they can
 * tell what the feature produces before producing any. It is drawn from their own catalogue - their brands, their
 * products, their team - so it reads as their shop rather than as stock art, and needs no shipped assets.
 *
 * None of it is presented as real: every consumer renders it blurred, behind copy that says the data is still
 * being collected.
 *
 * Nothing here uses `Math.random`. Selection is a deterministic pseudo-shuffle keyed off ids, which keeps the same
 * products on screen for the life of the catalogue rather than reshuffling on every emission of an unrelated
 * stream - and makes what a colleague sees on the same account the same as what you see.
 */
@Injectable() // Provided by Logged In Scope
export class HighGuideInsightsPlaceholderService extends BaseService {
  constructor(
    private companyDomainModel: CompanyDomainModel,
    private mediaManagerDomainModel: MediaManagerDomainModel,
    private productDomainModel: ProductDomainModel
  ) {
    super();
  }

  // Matched to the counts the API returns, so the blurred preview is the size of the thing it previews.
  static readonly nTrendingBrands = 10;
  static readonly nCollectionBrands = 8;
  static readonly nCollectionProducts = 12;
  static readonly nRecommendations = 12;

  private static readonly brandAssetTypeSortOrder = [
    AssetLibraryType.PrimaryBrand,
    AssetLibraryType.AlternateBrand,
    AssetLibraryType.Brand
  ].join(',');

  /**
   * How many brands the previews may draw from.
   *
   * One bound, exposed as `previewBrands$`, rather than a cap per consumer: the logo fetch and the widgets have to
   * agree on the same set, and when they did not, a tab could rotate onto a brand no logo had been fetched for and
   * silently fall back to its name.
   */
  private static readonly maxPreviewBrands = 12;

  /** Drawn from once per collection and once for the recommendation row, so it has to cover the largest of those. */
  private static readonly variantPoolSize = 30;

  /** Matches how budsense-high-guide keys brands, so "Pure Sunfarms" and "pure sunfarms " resolve to one logo. */
  static brandKey(brand: string): string {
    return brand?.stripWhiteSpaceAndLowerCase()?.trim();
  }

  /**
   * A stable ordering key. Two variants never collide meaningfully because a tie just preserves catalogue order.
   */
  private static stableOrder(value: string): number {
    let hash = 0;
    for (let index = 0; index < (value?.length ?? 0); index++) {
      hash = (hash * 31 + value.charCodeAt(index)) | 0;
    }
    return hash;
  }

  private readonly variants$ = this.productDomainModel.currentLocationVariants$;

  /** Brands the company carries most, which is the closest thing they have to a brand that would trend. */
  public readonly topBrands$: Observable<string[]> = this.variants$.pipe(
    map(variants => {
      const countsByBrand = new Map<string, number>();
      (variants ?? []).forEach(variant => {
        const brand = variant?.brand?.trim();
        if (!brand) return;
        countsByBrand.set(brand, (countsByBrand.get(brand) ?? 0) + 1);
      });
      return Array.from(countsByBrand.entries())
        .sort(([brandA, countA], [brandB, countB]) => countB - countA || brandA.localeCompare(brandB))
        .map(([brand]) => brand);
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  /**
   * One variant per brand to borrow imagery from.
   *
   * There is no brand logo anywhere in the dashboard - the real widgets get theirs from the insights payload - so
   * a brand tile in the empty state shows a product shot from that brand instead. A brand whose variants have no
   * imagery falls back to its name, which is what the tiles already do for a brand with no logo.
   */
  public readonly representativeVariantByBrand$: Observable<Map<string, Variant>> = this.variants$.pipe(
    map(variants => {
      const representatives = new Map<string, Variant>();
      (variants ?? []).forEach(variant => {
        const brand = variant?.brand?.trim();
        if (!brand || representatives.has(brand)) return;
        representatives.set(brand, variant);
      });
      return representatives;
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  /** The pool every product slot in every empty state draws from. */
  public readonly variantPool$: Observable<Variant[]> = this.variants$.pipe(
    map(variants => {
      return (variants ?? [])
        .shallowCopy()
        .sort(
          (a, b) =>
            HighGuideInsightsPlaceholderService.stableOrder(a?.id) -
            HighGuideInsightsPlaceholderService.stableOrder(b?.id)
        )
        .slice(0, HighGuideInsightsPlaceholderService.variantPoolSize);
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  /**
   * Teammates to attribute the placeholder recommendations to.
   *
   * `employees$` is typed `User[]` but is populated from `adminGetUsers$`, which returns `HydratedUser` - so the
   * profile pictures are there at runtime and survive the re-deserialize into `ProductSignalUser`. The fetch can
   * fail for a non-admin, which leaves this empty; the cards drop the author line rather than inventing a name.
   */
  public readonly authorPool$: Observable<ProductSignalUser[]> = combineLatest([
    this.companyDomainModel.employees$
  ]).pipe(
    map(([employees]) => {
      const Deserialize = window?.injector?.Deserialize;
      return (employees ?? [])
        .shallowCopy()
        .sort(
          (a, b) =>
            HighGuideInsightsPlaceholderService.stableOrder(a?.userId) -
            HighGuideInsightsPlaceholderService.stableOrder(b?.userId)
        )
        .map(employee => Deserialize?.instanceOf(ProductSignalUser, employee))
        .filter(author => exists(author));
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  /**
   * Brand logos for the placeholder tiles, keyed by the same normalized brand name the tiles look up.
   *
   * Brand imagery is not part of any product payload - it is a separate asset-library category hanging off a
   * variant. This is the request budsense-high-guide makes for its brand filter bar: the same three categories in
   * the same order, one asset per variant, and the first asset seen for a brand wins.
   *
   * Only the top brands are asked for, since only those are ever drawn. A brand with no logo on file is simply
   * absent, and its tile falls back to the brand name.
   */
  /** The brands every preview draws from, and exactly the set logos are fetched for. */
  public readonly previewBrands$: Observable<string[]> = this.topBrands$.pipe(
    map(topBrands => (topBrands ?? []).slice(0, HighGuideInsightsPlaceholderService.maxPreviewBrands)),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  public readonly brandLogos$: Observable<Map<string, Asset>> = combineLatest([
    this.previewBrands$,
    this.representativeVariantByBrand$
  ]).pipe(
    map(([previewBrands, representativeVariantByBrand]) => {
      return (previewBrands ?? [])
        .map(brand => ({ brand, variant: representativeVariantByBrand?.get(brand) }))
        .filter(entry => exists(entry.variant?.id));
    }),
    switchMap(brandsWithVariants => {
      if (!brandsWithVariants.length) return of(new Map<string, Asset>());
      const variantIds = brandsWithVariants.map(entry => entry.variant.id);
      return this.mediaManagerDomainModel
        .getVariantAssets$(variantIds, false, 1, HighGuideInsightsPlaceholderService.brandAssetTypeSortOrder)
        .pipe(
          map(assetsByVariantId => {
            const logos = new Map<string, Asset>();
            brandsWithVariants.forEach(({ brand, variant }) => {
              const logo = (assetsByVariantId?.get(variant.id) ?? []).find(asset => asset?.isImage());
              if (exists(logo)) logos.set(HighGuideInsightsPlaceholderService.brandKey(brand), logo);
            });
            return logos;
          }),
          // A missing logo is a fallback to the brand name, not something worth failing the preview over.
          catchError(() => of(new Map<string, Asset>()))
        );
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );
}
